// src/pages/support/ContactUs.tsx

import React, {
  FormEvent,
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import "./SupportPages.css";


// =====================================================
// CONFIGURATION
// =====================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


// =====================================================
// TYPES
// =====================================================

interface SupportTicket {
  id: string;
  ticketNumber: string;
  status: string;
  createdAt: string;
}

interface ContactResponse {
  success: boolean;
  message: string;
  ticket?: SupportTicket;
}


// =====================================================
// CONTACT US
// =====================================================

const ContactUs: React.FC = () => {

  const [name, setName] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [subject, setSubject] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [notice, setNotice] =
    useState("");

  const [ticketNumber, setTicketNumber] =
    useState("");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [submitStatus, setSubmitStatus] =
    useState<
      "idle" |
      "success" |
      "error"
    >("idle");


  // ===================================================
  // SUBMIT
  // ===================================================

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>
  ) => {

    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setNotice("");
    setTicketNumber("");

    try {

      const response =
        await fetch(
          `${API_BASE_URL}/api/support/contact`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              ...(
                localStorage.getItem("token")
                  ? {
                      Authorization:
                        `Bearer ${localStorage.getItem("token")}`,
                    }
                  : {}
              ),
            },

            body: JSON.stringify({
              name: name.trim(),
              email: email.trim(),
              subject,
              message: message.trim(),
            }),
          }
        );

      const data: ContactResponse =
        await response.json();

      if (
        !response.ok ||
        !data.success
      ) {

        throw new Error(
          data.message ||
          "Unable to send your message."
        );
      }

      setSubmitStatus("success");

      setNotice(
        data.message ||
        "Your message has been sent to P-ARENA Support."
      );

      if (data.ticket?.ticketNumber) {
        setTicketNumber(
          data.ticket.ticketNumber
        );
      }

      // Clear form only after backend success.

      setName("");
      setEmail("");
      setSubject("");
      setMessage("");

    } catch (error) {

      console.error(
        "Contact support error:",
        error
      );

      setSubmitStatus("error");

      setNotice(
        error instanceof Error
          ? error.message
          : "Unable to send your message. Please try again."
      );

    } finally {

      setIsSubmitting(false);

    }
  };


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="support-page">

      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section className="support-hero support-hero-compact">

        <div className="support-hero-glow" />

        <div className="support-hero-content">

          <nav
            className="support-breadcrumb"
            aria-label="Breadcrumb"
          >

            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <span>
              Support
            </span>

            <span>/</span>

            <strong>
              Contact Us
            </strong>

          </nav>


          <div className="support-emblem">
            ♦
          </div>

          <p className="support-eyebrow">
            P-ARENA SUPPORT
          </p>

          <h1>
            Contact Us
          </h1>

          <p className="support-hero-description">
            Have a question about P-ARENA,
            your account, gameplay, or the
            platform? Send us a message.
          </p>

        </div>

      </section>


      {/* ============================================= */}
      {/* CONTENT                                       */}
      {/* ============================================= */}

      <div className="support-content">

        <section className="support-contact-layout">

          {/* ========================================= */}
          {/* LEFT                                      */}
          {/* ========================================= */}

          <aside className="support-contact-info">

            <p className="support-mini-label">
              CONTACT P-ARENA
            </p>

            <h2>
              How Can We Help?
            </h2>

            <p>
              Use this form for general questions
              about P-ARENA, your account,
              gameplay, or platform features.
            </p>


            <div className="support-contact-option">

              <span>
                ?
              </span>

              <div>

                <h3>
                  Looking for an Answer?
                </h3>

                <p>
                  Check the Help Center before
                  sending a support request.
                </p>

                <Link to="/help">
                  Visit Help Center →
                </Link>

              </div>

            </div>


            <div className="support-contact-option">

              <span>
                !
              </span>

              <div>

                <h3>
                  Found a Technical Problem?
                </h3>

                <p>
                  Use our dedicated problem
                  reporting page for bugs and
                  gameplay issues.
                </p>

                <Link to="/report">
                  Report a Problem →
                </Link>

              </div>

            </div>

          </aside>


          {/* ========================================= */}
          {/* FORM                                      */}
          {/* ========================================= */}

          <div className="support-form-card">

            <div className="support-form-heading">

              <small>
                MESSAGE
              </small>

              <h2>
                Send Us a Message
              </h2>

              <p>
                Complete the form below with
                your question.
              </p>

            </div>


            <form
              className="support-form"
              onSubmit={handleSubmit}
            >

              <div className="support-form-row">

                <div className="support-field">

                  <label htmlFor="contact-name">
                    Name
                  </label>

                  <input
                    id="contact-name"
                    type="text"
                    value={name}
                    onChange={(event) =>
                      setName(
                        event.target.value
                      )
                    }
                    placeholder="Your name"
                    maxLength={100}
                    disabled={isSubmitting}
                    required
                  />

                </div>


                <div className="support-field">

                  <label htmlFor="contact-email">
                    Email
                  </label>

                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(event) =>
                      setEmail(
                        event.target.value
                      )
                    }
                    placeholder="you@example.com"
                    maxLength={255}
                    disabled={isSubmitting}
                    required
                  />

                </div>

              </div>


              <div className="support-field">

                <label htmlFor="contact-subject">
                  Subject
                </label>

                <select
                  id="contact-subject"
                  value={subject}
                  onChange={(event) =>
                    setSubject(
                      event.target.value
                    )
                  }
                  disabled={isSubmitting}
                  required
                >

                  <option value="">
                    Select a subject
                  </option>

                  <option value="account">
                    Account Question
                  </option>

                  <option value="gameplay">
                    Gameplay Question
                  </option>

                  <option value="multiplayer">
                    Multiplayer
                  </option>

                  <option value="profile">
                    Player Profile
                  </option>

                  <option value="privacy">
                    Privacy
                  </option>

                  <option value="other">
                    Other
                  </option>

                </select>

              </div>


              <div className="support-field">

                <label htmlFor="contact-message">
                  Message
                </label>

                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(event) =>
                    setMessage(
                      event.target.value
                    )
                  }
                  placeholder="Tell us how we can help..."
                  rows={8}
                  maxLength={3000}
                  disabled={isSubmitting}
                  required
                />

                <span className="support-character-count">
                  {message.length} / 3000
                </span>

              </div>


              {/* ===================================== */}
              {/* SERVER RESPONSE                       */}
              {/* ===================================== */}

              {notice && (

                <div
                  className={`
                    support-form-notice
                    ${
                      submitStatus === "success"
                        ? "support-form-notice-success"
                        : ""
                    }
                    ${
                      submitStatus === "error"
                        ? "support-form-notice-error"
                        : ""
                    }
                  `}
                  role={
                    submitStatus === "error"
                      ? "alert"
                      : "status"
                  }
                >

                  <strong>

                    {submitStatus === "success"
                      ? "Message Sent"
                      : "Unable to Send Message"}

                  </strong>

                  <p>
                    {notice}
                  </p>


                  {ticketNumber && (

                    <p className="support-ticket-number">

                      Ticket Number:{" "}

                      <strong>
                        {ticketNumber}
                      </strong>

                    </p>

                  )}

                </div>

              )}


              <button
                type="submit"
                className="support-submit-button"
                disabled={isSubmitting}
              >

                {isSubmitting
                  ? "Sending..."
                  : "Send Message"}

              </button>

            </form>

          </div>

        </section>

      </div>

    </main>
  );
};


export default ContactUs;